<template>
<div>
    <dailywork-form :getDailyworkList="getDailyworkList" ref="dailyworkFormModal"></dailywork-form>
    <invoice-form :invoiceFormPropsFunctions="invoiceFormPropsFunctions" :getDailyworkList="getDailyworkList" ref="invoiceForm"></invoice-form>
    <b-row>
        <b-col class="col-12">
            <div style="float:left;" class="custom-search d-flex justify-content-end">
                <b-form-group class="d-flex align-items-center">
                    <b-form-checkbox v-model="notInvoiced" @input="changeNotInvoice()" name="check-button" switch inline>
                        {{ $t('dailywork.not_invoiced') }}
                    </b-form-checkbox>
                </b-form-group>
            </div>

            <div style="float:right;" class="custom-search d-flex justify-content-end">
                <b-form-group>
                    <div class="d-flex align-items-center">
                        <b-button style="float:right; border-color:#6B7280; color:#374151;" size="sm" class="mr-1" variant="outline-secondary" @click="addNewDailywork">{{ $t('dailywork.add') }}</b-button>
                        <b-button variant="outline-primary" size="sm" @click.prevent="showInvoiceForm()" :disabled="selectionCount === 0" > {{$t('dailywork.invoice')}} </b-button>
                    </div>
                </b-form-group>
            </div>
        </b-col>
    </b-row>
    <!-- table -->
    <vue-good-table id="dailyworkTable" ref="dailyworkTable" :columns="columns" :rows="rows" :rowStyleClass="rowClass" styleClass="vgt-table condensed tableSize" :search-options="{
        enabled: true,
        externalQuery: searchTerm
      }" :select-options="{
        enabled: false,
        selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
        selectionInfoClass: 'custom-class',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true, // disable the select info panel on top
        selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
      }" :pagination-options="{
        enabled: true,
        perPage: pageLength
      }" theme="my-theme">
        <div slot="emptystate">
            {{ $t('dataTable.data_not_found') }}
        </div>
        <!-- Row: Table Row -->
        <template slot="table-column" slot-scope="props">
            <span v-if="props.column.field === 'client'" class="text-nowrap">
                {{ $t('dailywork.client') }}
            </span>
            <span v-else-if="props.column.field === 'clientAccounting'" class="text-nowrap">
                {{ $t('dailywork.client_accounting') }}
            </span>
            <span v-else-if="props.column.field === 'lawBillingStatus'" class="text-nowrap">
                {{ $t('dailywork.type') }}
            </span>
            <span v-else-if="props.column.field === 'paymentType'" class="text-nowrap">
                {{ $t('dailywork.payment_type') }}
            </span>
            <span v-else-if="props.column.field === 'currency'" class="text-nowrap">
                {{ $t('dailywork.currency') }}
            </span>
            <span v-else-if="props.column.field === 'recordDate'" class="text-nowrap">
                {{ $t('dailywork.date') }}
            </span>
            <span v-else-if="props.column.field === 'process'" class="text-nowrap"></span>
        </template>
        <!-- Column: Table Column -->
        <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field === 'select'">
                <b-form-checkbox
                    :disabled="props.row.lawBillingStatusId === 2"
                    :checked="props.row._selected === true"
                    @input="val => onRowToggle(props.row, val)"
                    class="d-inline-block"
                />
              </span>
            <span v-else-if="props.column.field === 'debitNo'">
                <b-badge variant="primary" style="cursor: pointer;" @click="getInvoiceInfo(props.row.lawInvoiceId)">
                    <feather-icon icon="MousePointerIcon" class="mr-25" />
                    <span>{{ props.row.debitNo }}</span>
                </b-badge>
            </span>
            <span v-else-if="props.column.field === 'agent'">
                <span>{{props.row.agent}}</span>
            </span>
            <span v-else-if="props.column.field === 'invoiceClientAccounting'">
                <span>{{ props.row.invoiceClientAccounting }} </span>
            </span>
            <span v-else-if="props.column.field === 'lawBillingStatus'" style="cursor:pointer" :title="props.row.billingStatus">
                <feather-icon v-if="props.row.lawBillingStatusId == 1" color="green" icon="CircleIcon" />
                <feather-icon v-if="props.row.lawBillingStatusId == 2" color="red" icon="CircleIcon" />
                <feather-icon v-if="props.row.lawBillingStatusId == 3" color="orange" icon="CircleIcon" />
            </span>

            <span v-else-if="props.column.field === 'paymentType'">
                <span>{{ props.row.paymentType }} </span>
            </span>
            <span v-else-if="props.column.field === 'currency'">
                <span>{{ props.row.currency }} </span>
            </span>
            <span v-else-if="props.column.field === 'recordDate'">
                <span>{{ $formatFn(props.row.recordDate) }} </span>
            </span>
            <span v-else-if="props.column.field === 'lawService'">
                <span>{{ props.row.lawService }} </span>
            </span>
            <span v-else-if="props.column.field === 'count'">
                <span>{{ props.row.count }} </span>
            </span>
            <span v-else-if="props.column.field === 'unitPrice'">
                <span>{{ $formatMoneyTr(props.row.unitPrice) }} </span>
            </span>
            <span v-else-if="props.column.field === 'price'">
                <span>{{ $formatMoneyTr(props.row.price) }} </span>
            </span>
            <span v-else-if="props.column.field === 'employee'">
                <span>{{ props.row.employee }} </span>
            </span>
            <span v-else-if="props.column.field === 'description'" v-b-tooltip.hover="{ title: props.row.description, customClass: 'wh-tooltip' }" class="wh-clamp-2">
                {{ props.row.description }}
            </span>
            <span v-else-if="props.column.field === 'process'">
                <list-actions-dropdown :actions="[
                    { icon: 'EditIcon', label: $t('others.edit'), handler: () => getInfo(props.row.id,props.row) },
                    { icon: 'XIcon', label: $t('others.delete'), color: '#DC2626', handler: () => deleted(props.row) },
                    ...(canArchive ? [{ icon: 'ArchiveIcon', label: $t('others.archive'), color: '#6B7280', handler: () => archiveRecord(props.row) }] : [])
                ]" />
            </span>
        </template>

        <!-- pagination -->
        <template slot="pagination-bottom" slot-scope="props">
            <div class="d-flex justify-content-between flex-wrap">
                <div class="d-flex align-items-center mb-0 mt-1">
                    <span class="text-nowrap"> {{ $t('dataTable.total') }} {{ props.total }} {{ $t('dataTable.record') }}
                    </span>
                    <b-form-select v-model="pageLength" :options="['10', '25', '50', '100']" class="mx-1" @input="(value) => props.perPageChanged({ currentPerPage: value })" />
                    <span class="text-nowrap"> {{ $t('dataTable.row_is_show') }} </span>
                </div>
                <div>
                    <b-pagination :value="1" :total-rows="props.total" :per-page="pageLength" first-number last-number align="right" prev-class="prev-item" next-class="next-item" class="mt-1 mb-0" @input="(value) => props.pageChanged({ currentPage: value })">
                        <template #prev-text>
                            <feather-icon icon="ChevronLeftIcon" size="18" />
                        </template>
                        <template #next-text>
                            <feather-icon icon="ChevronRightIcon" size="18" />
                        </template>
                    </b-pagination>
                </div>
            </div>
        </template>
    </vue-good-table>
</div>
</template>

  
<script>
export default {
    props: {
        getMatterList: {
            type: Function
        },
        lawMatterId: {
            type: Number
        },
        recordNo: {
            type: String
        },
        shortName: {
            type: String
        },
        invoiceDetailList: {
            type: Function
        }
    },
    data() {
        return {
            pageLength: 50,
            dir: false,
            checkedStatus: true,
            invoiceFormPropsFunctions: 2,
            columns: [
                {
                    label: '',
                    field: 'select',
                    thClass: 'text-center',
                    tdClass: 'text-center',
                },
                {
                    label: this.$t('dailywork.date'),
                    field: 'recordDate',
                    formatFn: this.$formatFn
                },
                {
                    label: this.$t('dailywork.service'),
                    field: `lawService`,
                },
                {
                    label: this.$t('dailywork.description'),
                    field: `description`,
                },
                {
                    label: this.$t('dailywork.currency'),
                    field: `currency`,
                },
                {
                    label: this.$t('dailywork.amount'),
                    field: `price`,
                    tdClass: 'text-right',
                    thClass: 'text-right',
                },
                {
                    label: this.$t('dailywork.employee'),
                    field: `employee`,
                },
                {
                    label: this.$t('dailywork.type'),
                    field: `lawBillingStatus`,
                },
                {
                    label: '',
                    field: 'process',
                    width: '10em',
                    sortable: false
                },
            ],
            rows: [],
            searchTerm: '',
            notInvoiced: true,
            selectedRows: [],
            selectedRowsArray: [],
            selectedIds: [],
            selectionCount: 0
        }
    },
    mounted() {
        this.getDailyworkList()
    },
    computed: {
        // Arşivle butonu yalnızca yetkili kullanıcılara (EmployeeId 1 ve 12) görünür.
        canArchive() {
            return [1, 12].includes(parseInt(localStorage.getItem('EmployeeId')))
        }
    },
    methods: {
        rowClass(row) {
            return row.billingStatusId === 2 ? 'blocked-selection' : '';
         },
         onRowToggle(row, val) {
            if (row.lawBillingStatusId === 2) return
            if (row._selected === val) return
            this.$set(row, '_selected', val)
            if (val) {
                if (!this.selectedIds.includes(row.id)) this.selectedIds.push(row.id)
            } else {
                this.selectedIds = this.selectedIds.filter(id => id !== row.id)
            }
            this.selectionCount = this.selectedIds.length
        },
        showInvoiceForm() {
            const picked = this.rows.filter(r => this.selectedIds.includes(r.id))
            if (picked.length === 0) {
                this.$ErrorSaveAlert()
                return
            }
            this.$refs.invoiceForm.showInfoFromDailyWorkList(picked)
            this.rows.forEach(r => this.$set(r, '_selected', false))
            this.selectedIds = []
            this.selectionCount = 0
        },
        async deleted(data) {
            data = {
                id: data.id
            }
            const controller = {
                name: 'LawDailywork'
            }
            const payload = {
                data: data,
                controller: controller
            }
            this.$DeleteConfirm().then(result => {
                if (result.value) {
                    this.$store.dispatch('moduleAuth/delete', payload).then((response) => {
                        if (response.data.resultStatus == true) {
                            this.$DeleteAlert()
                            this.getDailyworkList()
                        } else {
                            this.$WarningAlert(response.data.resultMessage)
                            this.getDailyworkList()
                        }
                    })
                }
            })
        },
        // Arşivle: müvekkile charge edilmez ama dosyada durur (IsArchived=true); listeden kalkar,
        // bütçe raporunda görünmeye devam eder. Backend: LawDailywork/ArchiveDailyWork.
        async archiveRecord(row) {
            const result = await this.$ArchiveConfirm();
            if (result.value) {
                const controller = { name: 'LawDailywork', actionName: 'ArchiveDailyWork' }
                const data = { id: row.id }
                const payload = { data: data, controller: controller }
                const response = await this.$store.dispatch('moduleAuth/customService', payload);
                if (response.data.resultStatus == true) {
                    this.$ArchiveAlert()
                    this.getDailyworkList()
                } else {
                    this.$WarningAlert(response.data.resultMessage)
                    this.getDailyworkList()
                }
            }
        },
        async addNewDailywork() {
            const controller = { name: 'LawMatterMatterTypeHistory', actionName: 'GetLawMatterMatterTypeHistoryListByLawMatterId' }
            const payload = { controller, data: { id: this.lawMatterId } }
            const response = await this.$store.dispatch('moduleAuth/customService', payload)
            if (response.data.resultStatus && response.data.lawMatterMatterTypeHistoryList.length === 0) {
                this.$MatterProcesssesEmptyAlert()
                return
            }
            this.getInfo(0)
        },
        async getInfo(id) {
            this.$refs.dailyworkFormModal.showInfo(id, this.lawMatterId, this.recordNo,this.shortName)
        },
        getDailyworkList() {
            this.rows = []
            const data = {
                pageLength: this.pageLength,
                pageNo: this.pageNo,
                recordId: this.lawMatterId,
                isNotInvoice: this.notInvoiced
            }
            const controller = {
                name: 'LawDailywork'
            }
            const payload = {
                controller: controller,
                data: data
            }
            this.$store.dispatch('moduleAuth/getList', payload).then((response) => {
                if (response.data.resultStatus == true) {
                    response.data.lawDailyworkList.forEach(element => {
                        this.rows.push(element)
                    })
                } else {
                    this.$WarningAlert(response.data.resultMessage)
                }
            })
        },

        changeNotInvoice() {
            if (this.notInvoiced) {
                this.checkedStatus = true
                this.columns = [{
                    label: '',
                    field: 'select',
                    thClass: 'text-center',
                    tdClass: 'text-center',
                },
                    {
                        label: this.$t('dailywork.date'),
                        field: 'recordDate',
                        formatFn: this.$formatFn
                    },
                    {
                        label: this.$t('dailywork.service'),
                        field: `lawService`,
                    },
                    {
                        label: this.$t('dailywork.description'),
                        field: `description`,
                    },
                    {
                        label: this.$t('dailywork.currency'),
                        field: `currency`,
                    },
                    {
                        label: this.$t('dailywork.amount'),
                        field: `price`,
                        tdClass: 'text-right',
                        thClass: 'text-right',
                    },
                    {
                        label: this.$t('dailywork.employee'),
                        field: `employee`,
                    },
                    {
                        label: this.$t('dailywork.type'),
                        field: `lawBillingStatus`,
                    },
                    {
                        label: '',
                        field: 'process',
                        width: '10em',
                        sortable: false
                    },
                ]
            } else {
                this.checkedStatus = false
                this.columns = [{
                        label: this.$t('dailywork.debitNo'),
                        field: `debitNo`,
                    },
                    {
                        label: this.$t('dailywork.date'),
                        field: 'recordDate',
                        formatFn: this.$formatFn
                    },
                    {
                        label: this.$t('dailywork.service'),
                        field: `lawService`,
                    },
                    {
                        label: this.$t('dailywork.description'),
                        field: `description`,
                    },
                    {
                        label: this.$t('dailywork.currency'),
                        field: `currency`,
                    },
                    {
                        label: this.$t('dailywork.amount'),
                        field: `price`,
                        tdClass: 'text-right',
                        thClass: 'text-right',
                    },
                    {
                        label: this.$t('dailywork.employee'),
                        field: `employee`,
                    },
                    {
                        label: this.$t('dailywork.type'),
                        field: `lawBillingStatus`,
                    },
                ]
            }

            this.getDailyworkList()
        },
        changeChecked(id, target) {
            const status = target.checked
            if (status) {
                var dailyworkControl = this.rows.find(p => p.id == id)
                if (dailyworkControl.billingStatusId == 2) {
                    this.$WarningAlert(this.$t('others.NotSelectedDoNotInvoice'))
                    target.checked = false;
                    return
                }
            }
        },
        async getInvoiceInfo(id) {
            this.$refs.invoiceForm.showInfo(id)
        },
    },
}
</script>
  
<style lang="scss">
.tableSize {
    font-size: 13px !important;
    border-collapse: collapse;
    width: 100%;
    max-width: 100%;
    table-layout: auto;
}
</style>
